'use client';

import { bunja, createScope } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { PropsWithChildren } from 'react';

import {
  R230522NormalSubContext,
  R230522NormalSubScope,
  R230522NormalSubSocket,
  nullSocket as normalSubNullSocket,
} from '@flipster/r230522/normal-sub/socket';
import { r230522FixedSubEmulatorBunja } from '@flipster/r230522/normal-sub/socket/fixed-sub-emulator';
import { R230522Scope, nullSocket } from '@flipster/r230522/original/internal/context';
import { R230522Context } from '@flipster/r230522/original/socket';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { authBunja, nutsBunja } from '../shared/auth/state';
import { webviewInfoBunja } from '../shared/webview';
import { DatadogR230522Setup } from './datadog';
import { bridgeSocketBunja } from './instance/bridge-socket';
import { createPrivateNormalSub230522Socket, createPublicNormalSub230522Socket } from './instance/web-socket';

export type SocketType = 'bridge' | 'websocket';

export interface R230522ProviderProps extends PropsWithChildren {
  preferredSocketType?: SocketType;
}
export const R230522Provider = ({ children, preferredSocketType }: R230522ProviderProps) => {
  const { normalSubSocketAtom } = useBunja(r230522NormalSubProviderBunja);
  const normalSubSocket = useAtomValue(normalSubSocketAtom);
  return (
    <R230522NormalSubContext value={normalSubSocket}>
      <R230522FixedSubProvider preferredSocketType={preferredSocketType}>{children}</R230522FixedSubProvider>
    </R230522NormalSubContext>
  );
};

const R230522FixedSubProvider = ({ children, preferredSocketType }: R230522ProviderProps) => {
  const { fixedSubSocketAtom } = useBunja(r230522FixedSubProviderBunja, [
    PreferredSocketTypeScope.bind(preferredSocketType),
  ]);
  const fixedSubSocket = useAtomValue(fixedSubSocketAtom);
  return (
    <R230522Context value={fixedSubSocket}>
      <DatadogR230522Setup />
      {children}
    </R230522Context>
  );
};

const PreferredSocketTypeScope = createScope<SocketType | undefined>();

const r230522NormalSubProviderBunja = bunja(() => {
  const parentSocket = bunja.use(R230522NormalSubScope);
  if (parentSocket !== normalSubNullSocket) throw new Error('R230522NormalSubContext is already wrapped');

  bunja.use(reconnectPrivateSocketOnNutsChangeBunja);

  const { publicWebNormalSubSocket, privateWebNormalSubSocket } = bunja.use(r230522InstancesBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const webNormalSubSocketAtom = atom((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    if (isCookieLoggedIn) return privateWebNormalSubSocket;
    else return publicWebNormalSubSocket;
  });

  const normalSubSocketAtom = atom<R230522NormalSubSocket>((get) => {
    const webNormalSubSocket = get(webNormalSubSocketAtom) as unknown as R230522NormalSubSocket;
    return webNormalSubSocket;
  });

  return { normalSubSocketAtom };
});
r230522NormalSubProviderBunja.debugLabel = 'r230522NormalSubProviderBunja';

const r230522FixedSubProviderBunja = bunja(() => {
  const parentSocket = bunja.use(R230522Scope);
  if (parentSocket !== nullSocket) throw new Error('R230522Context is already wrapped');

  const { r230522FixedSubSocket } = bunja.use(r230522FixedSubEmulatorBunja);
  const preferredSocketType = bunja.use(PreferredSocketTypeScope);
  const { webviewAtom } = bunja.use(webviewInfoBunja);
  const { bridgeFixedSubSocket, hasBridgeConnectionErrorAtom } = bunja.use(r230522InstancesBunja);

  const fixedSubSocketAtom = atom((get) => {
    const isWebview = get(webviewAtom);
    const hasBridgeConnectionError = get(hasBridgeConnectionErrorAtom);
    if (hasBridgeConnectionError) return r230522FixedSubSocket;
    if (preferredSocketType === 'websocket') return r230522FixedSubSocket;
    if (preferredSocketType === 'bridge') return bridgeFixedSubSocket;
    if (isWebview) return bridgeFixedSubSocket;
    return r230522FixedSubSocket;
  });

  return { fixedSubSocketAtom };
});
r230522FixedSubProviderBunja.debugLabel = 'r230522FixedSubProviderBunja';

const r230522InstancesBunja = bunja(() => {
  const publicWebNormalSubSocket = createPublicNormalSub230522Socket();
  const privateWebNormalSubSocket = createPrivateNormalSub230522Socket();
  const { bridgeFixedSubSocket, hasBridgeConnectionErrorAtom } = bunja.use(bridgeSocketBunja);
  return {
    publicWebNormalSubSocket,
    privateWebNormalSubSocket,
    bridgeFixedSubSocket,
    hasBridgeConnectionErrorAtom,
  };
});
r230522InstancesBunja.debugLabel = 'r230522InstancesBunja';

// 다계정 전환 시(nuts A → nuts B) 이전 세션의 stale 소켓/데이터가 남지 않도록
// private 소켓을 닫고 페이지를 새로고침하여 새 세션으로 완전히 전환함.
const reconnectPrivateSocketOnNutsChangeBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { privateWebNormalSubSocket } = bunja.use(r230522InstancesBunja);
  const { cookieAtom } = bunja.use(nutsBunja);

  bunja.effect(() => {
    let previousNuts = store.get(cookieAtom);
    return store.sub(cookieAtom, () => {
      const currentNuts = store.get(cookieAtom);
      if (currentNuts === previousNuts) return;
      const hadValidNuts = previousNuts !== undefined;
      previousNuts = currentNuts;
      // 세션 A → 세션 B 전환만 처리. 로그아웃(→ undefined)이나 최초 로그인(undefined →)은 무시.
      if (hadValidNuts && currentNuts !== undefined) {
        privateWebNormalSubSocket.close();
        window.location.reload();
      }
    });
  });
});
reconnectPrivateSocketOnNutsChangeBunja.debugLabel = 'reconnectPrivateSocketOnNutsChangeBunja';
